import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import SignatureCanvas from "react-signature-canvas";
import { Typography } from "@mui/material";
import { closeOutline } from "ionicons/icons";
import {
  SIGNATURE_ROLE_LABELS,
  type SignatureCaptureResult,
  type SignatureRole,
} from "./types";
import "./SignatureCaptureModal.css";
import { isVerboseLoggingEnabled } from "../../utils/debug";

type SignatureCaptureModalProps = {
  isOpen: boolean;
  role: SignatureRole | null;
  onDismiss: () => void;
  onSave: (result: SignatureCaptureResult) => void;
};

type SignatureStrokeData = ReturnType<SignatureCanvas["toData"]>;

type CanvasMetrics = {
  cssWidth: number;
  cssHeight: number;
  attrWidth: number;
  attrHeight: number;
  devicePixelRatio: number;
  wrapperWidth: number | null;
  wrapperLeft: number | null;
};

const SignatureCaptureModal: React.FC<SignatureCaptureModalProps> = ({
  isOpen,
  role,
  onDismiss,
  onSave,
}) => {
  const signatureRef = useRef<SignatureCanvas | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [strokeHistory, setStrokeHistory] = useState<SignatureStrokeData>([]);
  const historyRef = useRef<SignatureStrokeData>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const canvasWrapperRef = useRef<HTMLDivElement | null>(null);
  const [canvasDimensions, setCanvasDimensions] = useState({
    width: 1024,
    height: 360,
  });
  const [canvasMetrics, setCanvasMetrics] = useState<CanvasMetrics | null>(null);
  const verbose = useMemo(() => isVerboseLoggingEnabled(), []);

  const logSignatureDebug = useCallback(
    (event: string, meta: Record<string, unknown> = {}) => {
      if (!verbose) {
        return;
      }
      // eslint-disable-next-line no-console
      console.debug("[SignatureCaptureModal]", event, { role, ...meta });
    },
    [role, verbose]
  );

  const captureCanvasMetrics = useCallback((): CanvasMetrics | null => {
    if (!signatureRef.current) {
      return null;
    }
    try {
      const canvas = signatureRef.current.getCanvas();
      const rect = canvas.getBoundingClientRect();
      const wrapperRect = canvasWrapperRef.current?.getBoundingClientRect();
      return {
        cssWidth: Number(rect.width.toFixed(2)),
        cssHeight: Number(rect.height.toFixed(2)),
        attrWidth: canvas.width,
        attrHeight: canvas.height,
        devicePixelRatio: Number((window.devicePixelRatio ?? 1).toFixed(2)),
        wrapperWidth: wrapperRect
          ? Number(wrapperRect.width.toFixed(2))
          : null,
        wrapperLeft: wrapperRect
          ? Number(wrapperRect.left.toFixed(2))
          : null,
      };
    } catch (_error) {
      return null;
    }
  }, []);

  const logCanvasMetrics = useCallback(
    (source: string) => {
      const metrics = captureCanvasMetrics();
      if (metrics) {
        console.info("[SignatureCaptureModal] canvas metrics", {
          source,
          ...metrics,
        });
        if (verbose) {
          logSignatureDebug(`canvas metrics (${source})`, metrics);
        }
        setCanvasMetrics(metrics);
      } else {
        console.info(
          "[SignatureCaptureModal] canvas metrics unavailable",
          source
        );
        if (verbose) {
          logSignatureDebug(`canvas metrics (${source}) unavailable`);
        }
      }
    },
    [captureCanvasMetrics, logSignatureDebug, verbose]
  );

  useEffect(() => {
    if (!verbose) {
      return;
    }
    // eslint-disable-next-line no-console
    console.debug("[SignatureCaptureModal] state", { isOpen, role });
  }, [isOpen, role, verbose]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mediaQuery = window.matchMedia("(orientation: landscape)");
    const update = () => setIsLandscape(mediaQuery.matches);
    update();
    const listener = (event: MediaQueryListEvent) => setIsLandscape(event.matches);
    mediaQuery.addEventListener
      ? mediaQuery.addEventListener("change", listener)
      : mediaQuery.addListener(listener);
    return () => {
      mediaQuery.removeEventListener
        ? mediaQuery.removeEventListener("change", listener)
        : mediaQuery.removeListener(listener);
    };
  }, []);

  const roleLabel = useMemo(() => {
    if (!role) return "Signature";
    return `${SIGNATURE_ROLE_LABELS[role]} Signature`;
  }, [role]);

  const canSave = strokeHistory.length > 0 && !isDrawing;
  const canUndo = strokeHistory.length > 0 && !isDrawing;

  const handleClear = useCallback(() => {
    logSignatureDebug("handleClear invoked");
    signatureRef.current?.clear();
    setError(null);
    historyRef.current = [];
    setStrokeHistory([]);
  }, [logSignatureDebug]);

  const handleDismiss = useCallback(() => {
    logSignatureDebug("handleDismiss invoked");
    handleClear();
    onDismiss();
  }, [handleClear, logSignatureDebug, onDismiss]);

  const handleSave = useCallback(() => {
    logSignatureDebug("handleSave invoked");
    if (!role) {
      logSignatureDebug("handleSave aborted: missing role");
      return;
    }
    if (!canSave) {
      logSignatureDebug("handleSave aborted: nothing to save", {
        strokes: strokeHistory.length,
        isDrawing,
      });
      return;
    }
    const canvas = signatureRef.current;
    if (!canvas || canvas.isEmpty()) {
      logSignatureDebug("handleSave aborted: empty canvas", { hasCanvas: Boolean(canvas) });
      setError("Draw the signature before continuing.");
      return;
    }
    const trimmed = canvas.getTrimmedCanvas();
    const dataUrl = trimmed.toDataURL("image/png");
    logSignatureDebug("handleSave captured signature", {
      dataUrlLength: dataUrl.length,
    });
    onSave({ role, dataUrl });
    handleClear();
  }, [canSave, handleClear, isDrawing, logSignatureDebug, onSave, role, strokeHistory.length]);

  const handleStrokeBegin = useCallback(() => {
    logSignatureDebug("onBegin stroke");
    logCanvasMetrics("onBegin");
    setIsDrawing(true);
    setError(null);
  }, [logCanvasMetrics, logSignatureDebug]);

  const handleStrokeEnd = useCallback(() => {
    logSignatureDebug("onEnd stroke");
    const data = signatureRef.current?.toData() ?? [];
    historyRef.current = data;
    setStrokeHistory(data);
    setIsDrawing(false);
  }, [logSignatureDebug]);

  useLayoutEffect(() => {
    if (!isOpen || !canvasWrapperRef.current) {
      return undefined;
    }
    const wrapper = canvasWrapperRef.current;

    const updateDimensions = (width: number) => {
      const clampedHeight = Math.max(320, Math.min(520, width * 0.45));
      setCanvasDimensions((current) => {
        if (current.width === width && current.height === clampedHeight) {
          return current;
        }
        return { width, height: clampedHeight };
      });
    };

    updateDimensions(wrapper.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      updateDimensions(entry.contentRect.width);
    });

    observer.observe(wrapper);
    return () => {
      observer.disconnect();
    };
  }, [isOpen]);

  const resizeCanvasToDisplaySize = useCallback(
    (source: string): boolean => {
      if (!signatureRef.current) {
        console.info(
          "[SignatureCaptureModal] resize skipped (missing signature ref)",
          source
        );
        return false;
      }
      const canvas = signatureRef.current.getCanvas();
      const context = canvas.getContext("2d");
      if (!context) {
        console.info(
          "[SignatureCaptureModal] resize skipped (missing context)",
          source
        );
        return false;
      }
      const displayWidth = Math.round(canvasDimensions.width);
      const displayHeight = Math.round(canvasDimensions.height);
      if (displayWidth <= 0 || displayHeight <= 0) {
        console.info(
          "[SignatureCaptureModal] resize skipped (zero display size)",
          { source, displayWidth, displayHeight }
        );
        return false;
      }
      const ratio = Math.max(window.devicePixelRatio ?? 1, 1);
      const pixelWidth = Math.round(displayWidth * ratio);
      const pixelHeight = Math.round(displayHeight * ratio);
      const sizeChanged =
        canvas.width !== pixelWidth || canvas.height !== pixelHeight;

      if (sizeChanged) {
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.scale(ratio, ratio);
        console.info("[SignatureCaptureModal] canvas dpi resize", {
          source,
          displayWidth,
          displayHeight,
          pixelWidth,
          pixelHeight,
          ratio,
        });
        if (verbose) {
          logSignatureDebug("dpi resize applied", {
            source,
            displayWidth,
            displayHeight,
            pixelWidth,
            pixelHeight,
            ratio,
          });
        }
      } else {
        console.info("[SignatureCaptureModal] canvas dpi resize skipped", {
          source,
          displayWidth,
          displayHeight,
          pixelWidth,
          pixelHeight,
          ratio,
        });
      }

      return sizeChanged;
    },
    [
      canvasDimensions.height,
      canvasDimensions.width,
      logSignatureDebug,
      verbose,
    ]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let canceled = false;

    const attemptResize = (tick: number) => {
      if (canceled) {
        return;
      }
      const resized = resizeCanvasToDisplaySize(
        `dimensions-effect#${tick.toString()}`
      );
      if (!signatureRef.current) {
        console.info(
          "[SignatureCaptureModal] resize retry (missing signatureRef)",
          { tick }
        );
        requestAnimationFrame(() => attemptResize(tick + 1));
        return;
      }
      if (resized) {
        signatureRef.current.clear();
        if (historyRef.current.length > 0) {
          signatureRef.current.fromData(historyRef.current);
        }
      } else if (historyRef.current.length > 0) {
        signatureRef.current.clear();
        signatureRef.current.fromData(historyRef.current);
      }
    };

    const raf = requestAnimationFrame(() => attemptResize(1));
    return () => {
      canceled = true;
      cancelAnimationFrame(raf);
    };
  }, [
    canvasDimensions.height,
    canvasDimensions.width,
    isOpen,
    resizeCanvasToDisplaySize,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setCanvasMetrics(null);
      return;
    }
    logCanvasMetrics("effect");
  }, [
    canvasDimensions.height,
    canvasDimensions.width,
    isOpen,
    logCanvasMetrics,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let frame = requestAnimationFrame(() => {
      logCanvasMetrics("raf");
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [isOpen, logCanvasMetrics]);

  useEffect(() => {
    if (!isOpen) {
      handleClear();
    }
  }, [handleClear, isOpen]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={handleDismiss}
      className="signature-modal"
      backdropDismiss={false}
    >
      {!isLandscape && (
        <IonHeader translucent>
          <IonToolbar className="signature-modal__toolbar">
            <IonButtons slot="start">
              <IonButton onClick={handleDismiss} color="medium" fill="clear">
                <IonIcon slot="icon-only" icon={closeOutline} aria-hidden="true" />
              </IonButton>
            </IonButtons>
            <IonTitle className="signature-modal__title">{roleLabel}</IonTitle>
          </IonToolbar>
        </IonHeader>
      )}
      <IonContent className="signature-modal__content" fullscreen>
        {isLandscape && (
          <div className="signature-modal__floating-close">
            <IonButton onClick={handleDismiss} color="medium" fill="clear">
              <IonIcon slot="icon-only" icon={closeOutline} aria-hidden="true" />
            </IonButton>
          </div>
        )}
        <div
          className="signature-modal__canvas-wrapper"
          ref={canvasWrapperRef}
          style={{ height: canvasDimensions.height }}
        >
          <SignatureCanvas
            ref={signatureRef}
            penColor="#111"
            backgroundColor="#fff"
            canvasProps={{
              className: "signature-modal__canvas",
              width: Math.round(canvasDimensions.width),
              height: Math.round(canvasDimensions.height),
            }}
            minWidth={0.5}
            maxWidth={2.5}
            throttle={10}
            velocityFilterWeight={0.6}
            onBegin={handleStrokeBegin}
            onEnd={handleStrokeEnd}
          />
        </div>
        <Typography
          variant="body2"
          color="textSecondary"
          className="signature-modal__hint"
        >
          Use your finger or Apple Pencil to sign. Rotate the device if you need
          more space.
        </Typography>
        <div className="signature-modal__status">
          <Typography variant="body2" color="textSecondary">
            {isDrawing
              ? "Keep your strokes fluid—lift your finger when finished."
              : canSave
              ? "Review your signature below."
              : "Draw your signature in the card above to continue."}
          </Typography>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </div>
      </IonContent>
      <IonFooter>
        <IonToolbar className="signature-modal__footer-toolbar">
          <IonButtons slot="start">
            <IonButton onClick={handleClear} color="medium" disabled={!canUndo}>
              Clear
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton
              strong
              onClick={handleSave}
              disabled={!canSave}
              color="primary"
            >
              Submit
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};

export default SignatureCaptureModal;
